import classNames from "classnames";
import React, { forwardRef } from "react";
import BoardAvatar from "./board-avatar";
import { formatDueDate, isOverdue } from "./due-date";
import { displayNameOf } from "./people-directory";
import TypeIcon from "./type-icon";
import { IssuePriority, IssueType, LabelDef, PRIORITY_LABELS, Project, StatusCategory } from "./types";

// The subset of issue fields a card renders — satisfied by both the full `Issue` and the
// light `IssueCard` DTO, so the card works whether it's fed a snapshot issue or a paged card.
// The count/date fields are optional so a caller holding a lighter shape still type-checks;
// absent reads the same as zero, i.e. the chip simply does not render.
export interface BoardCardIssue {
  id: string;
  key: string;
  summary: string;
  type: IssueType;
  priority: IssuePriority;
  assigneeUserId: string | null;
  labels: string[];
  storyPoints: number | null;
  statusId: string;
  descriptionMarkdown?: string;
  dueDate?: string | null;
  subtaskCount?: number;
  commentCount?: number;
  attachmentCount?: number;
}

export interface IssueCardProps {
  issue: BoardCardIssue;
  focused: boolean;
  dragging: boolean;
  showProjectChip: boolean;
  blocked: boolean;
  sprintName: string | null;
  labelDefs: LabelDef[];
  project: Project | undefined;
  /** The hosting column's category — a done card is never painted overdue. */
  statusCategory: StatusCategory;
  onClick: (issue: BoardCardIssue) => void;
  onContextMenu?: (issue: BoardCardIssue, x: number, y: number) => void;
  draggableProps?: React.HTMLAttributes<HTMLDivElement>;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  innerRef?: (element: HTMLElement | null) => void;
}

interface PriorityProps {
  priority: IssuePriority;
}

/**
 * The three-bar glyph plus its word. The glyph alone is a Linear convention, not a universal
 * one — "which of these two stacks is higher" is a poor way to read an ordinal scale you only
 * glance at, and the bars carry no colour at medium/low. The word is dropped in compact density
 * (SCSS), where the whole point is fitting more cards.
 */
const Priority: React.FC<PriorityProps> = ({ priority }) => (
  <span className="kuika-board__priority-chip" title={PRIORITY_LABELS[priority]}>
    <span className={`kuika-board__priority kuika-board__priority--${priority}`} aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
    <span className="kuika-board__priority-label">{PRIORITY_LABELS[priority]}</span>
  </span>
);

interface CardCountProps {
  className: string;
  label: string;
  value: number;
  children: React.ReactNode;
}

/** One icon-led count chip: the glyph, then the number, in the meta row's muted voice. */
const CardCount: React.FC<CardCountProps> = ({ className, label, value, children }) => (
  <span className={classNames("kuika-board__card-count", className)} title={label} aria-label={label}>
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
      {children}
    </svg>
    {value}
  </span>
);

const IssueCard = forwardRef<HTMLDivElement, IssueCardProps>(function IssueCard(props, ref) {
  const {
    issue,
    focused,
    dragging,
    showProjectChip,
    blocked,
    sprintName,
    labelDefs,
    project,
    statusCategory,
    onClick,
    onContextMenu,
    draggableProps,
    dragHandleProps,
    innerRef
  } = props;
  const visibleLabels: string[] = issue.labels.slice(0, 2);
  const remainingLabels: number = issue.labels.length - visibleLabels.length;
  const dueLabel: string | null = formatDueDate(issue.dueDate);
  const overdue: boolean = isOverdue(issue.dueDate, statusCategory === "done");
  const subtaskCount: number = issue.subtaskCount ?? 0;
  const commentCount: number = issue.commentCount ?? 0;
  const attachmentCount: number = issue.attachmentCount ?? 0;

  return (
    <div
      ref={(element: HTMLDivElement | null) => {
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = element;
        }
        innerRef?.(element);
      }}
      className={classNames("kuika-board__card", {
        "kuika-board__card--focused": focused && !dragging,
        "kuika-board__card--dragging": dragging
      })}
      data-issue-id={issue.id}
      data-testid={`board-card-${issue.key}`}
      role="button"
      aria-label={`${issue.key}: ${issue.summary}, priority ${PRIORITY_LABELS[issue.priority]}`}
      title={issue.descriptionMarkdown?.trim() ? issue.descriptionMarkdown.trim().slice(0, 280) : undefined}
      onClick={() => onClick(issue)}
      onContextMenu={(event: React.MouseEvent) => {
        if (!onContextMenu) return;
        event.preventDefault();
        onContextMenu(issue, event.clientX, event.clientY);
      }}
      {...draggableProps}
      {...dragHandleProps}
    >
      {/*
        The assignee sits with the summary, not at the tail of the meta row: "whose is this" is
        read down a column in one vertical sweep, and the meta row's tail moves with every chip
        the card happens to carry.
      */}
      <div className="kuika-board__card-head">
        <div className="kuika-board__card-summary">{issue.summary}</div>
        {issue.assigneeUserId ? (
          <BoardAvatar
            userId={issue.assigneeUserId}
            className="kuika-board__assignee-avatar"
            ariaLabel={`Assigned to ${displayNameOf(issue.assigneeUserId)}`}
            title={displayNameOf(issue.assigneeUserId)}
          />
        ) : (
          // An empty slot says nothing; a dashed one says "nobody yet" and holds the row's
          // geometry steady next to its assigned neighbours.
          <span
            className="kuika-board__assignee-avatar kuika-board__assignee-avatar--unassigned"
            title="Unassigned"
            aria-label="Unassigned"
          >
            <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <circle cx="6" cy="4.1" r="2.1" stroke="currentColor" strokeWidth="1.2" />
              <path d="M1.9 10.4a4.2 4.2 0 0 1 8.2 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </span>
        )}
      </div>
      <div className="kuika-board__card-meta">
        <TypeIcon type={issue.type} />
        {showProjectChip && project && (
          <span className="kuika-board__card-project-chip" title={project.name} aria-label={`Project ${project.name}`}>
            <span className="kuika-board__card-project-dot" style={{ background: project.color }} />
            {project.key}
          </span>
        )}
        <span className="kuika-board__card-key">{issue.key}</span>
        {sprintName && (
          <span className="kuika-board__card-sprint" title={`In ${sprintName}`}>
            <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.4" />
              <path d="M6 3 V6 L8 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            {sprintName}
          </span>
        )}
        {blocked && (
          <span className="kuika-board__card-blocked" title="Blocked by another issue">
            <svg viewBox="0 0 12 12" fill="none">
              <rect x="2.5" y="5" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
              <path d="M4 5 V3.5 A2 2 0 0 1 8 3.5 V5" stroke="currentColor" strokeWidth="1.4" />
            </svg>
            Blocked
          </span>
        )}
        {dueLabel && (
          <span
            className={classNames("kuika-board__card-due", { "kuika-board__card-due--overdue": overdue })}
            title={overdue ? `Overdue — was due ${dueLabel}` : `Due ${dueLabel}`}
            aria-label={overdue ? `Overdue, was due ${dueLabel}` : `Due ${dueLabel}`}
          >
            <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <rect x="1.6" y="2.4" width="8.8" height="8" rx="1.4" stroke="currentColor" strokeWidth="1.2" />
              <path d="M1.6 5H10.4M4 1.4v2M8 1.4v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            {dueLabel}
          </span>
        )}
        <Priority priority={issue.priority} />
        {visibleLabels.map((label: string) => {
          const def: LabelDef | undefined = labelDefs.find(
            (entry: LabelDef) => entry.name.toLowerCase() === label.toLowerCase()
          );
          return (
            <span
              key={label}
              className="kuika-board__label-pill"
              title={label}
              style={
                def
                  ? {
                      background: `color-mix(in srgb, ${def.color} 14%, transparent)`,
                      borderColor: `color-mix(in srgb, ${def.color} 26%, transparent)`,
                      color: def.color
                    }
                  : undefined
              }
            >
              {label}
            </span>
          );
        })}
        {remainingLabels > 0 && (
          <span className="kuika-board__label-pill" title={`+${remainingLabels} more`}>
            +{remainingLabels}
          </span>
        )}
        {/*
          Three counts the card DTO has always carried and never showed. They say how much work
          is hiding behind the summary — the difference between a one-liner and a thread with
          four subtasks — which is exactly what you want before opening anything.
        */}
        {subtaskCount > 0 && (
          <CardCount
            className="kuika-board__card-count--subtasks"
            label={`${subtaskCount} subtasks`}
            value={subtaskCount}
          >
            <path
              d="M2.6 2.6h6.8M2.6 6h6.8M2.6 9.4h4.2"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </CardCount>
        )}
        {commentCount > 0 && (
          <CardCount
            className="kuika-board__card-count--comments"
            label={`${commentCount} comments`}
            value={commentCount}
          >
            <path
              d="M10.4 6.6a3.8 3.8 0 0 1-3.8 3.8H4.2L1.6 12V4.2A3.8 3.8 0 0 1 5.4 .4h1.2a3.8 3.8 0 0 1 3.8 3.8z"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </CardCount>
        )}
        {attachmentCount > 0 && (
          <CardCount
            className="kuika-board__card-count--attachments"
            label={`${attachmentCount} attachments`}
            value={attachmentCount}
          >
            <path
              d="M8.6 5.2 5.1 8.7a1.7 1.7 0 0 1-2.4-2.4l4-4a2.6 2.6 0 0 1 3.7 3.7L6.2 10.2"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </CardCount>
        )}
        <span className="kuika-board__card-spacer" />
        {issue.storyPoints != null && <span className="kuika-board__story-points">{issue.storyPoints}</span>}
      </div>
    </div>
  );
});

export default IssueCard;
